import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  CONSTITUTIONS,
  SERVICES,
  EDITABLE_CLIENT_FIELDS,
  IDENTITY_FIELDS,
  CLIENT_FIELD_MAX,
  clientToForm,
} from "@/constants/clients";

const sameList = (a = [], b = []) =>
  a.length === b.length && a.every((x) => b.includes(x));

const nullIfBlank = (v) => (v && v.trim() ? v.trim() : null);

export default function ClientFormDialog({
  client = null,
  open,
  onOpenChange,
  onSaved,
}) {
  const isEdit = !!client;
  const [form, setForm] = useState(() => clientToForm(client));
  const [code, setCode] = useState(client?.client_code ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(clientToForm(client));
      setCode(client?.client_code ?? "");
      setReason("");
    }
  }, [open, client]);

  const original = clientToForm(client);
  const changed = EDITABLE_CLIENT_FIELDS.filter(
    (f) => (form[f] ?? "").trim() !== (original[f] ?? ""),
  );
  const servicesChanged = !sameList(
    form.services_availed,
    original.services_availed,
  );
  const identityChange =
    isEdit && changed.some((f) => IDENTITY_FIELDS.includes(f));
  const dirty = changed.length > 0 || servicesChanged;
  const reasonMissing = identityChange && reason.trim().length < 5;
  const canSubmit =
    !!form.name.trim() &&
    (isEdit ? dirty : !!code.trim()) &&
    !reasonMissing &&
    !busy;

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));
  const setUpper = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value.toUpperCase() }));

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services_availed: f.services_availed.includes(s)
        ? f.services_availed.filter((x) => x !== s)
        : [...f.services_availed, s],
    }));

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      if (isEdit) {
        const payload = Object.fromEntries(
          changed.map((f) => [f, form[f].trim()]),
        );
        if (servicesChanged) payload.services_availed = form.services_availed;
        await api.patch(`/clients/${client.id}`, payload, {
          headers: reason.trim() ? { "X-Change-Reason": reason.trim() } : {},
        });
        toast.success("Client updated");
      } else {
        await api.post("/clients", {
          client_code: code.trim(),
          name: form.name.trim(),
          constitution: form.constitution,
          ...Object.fromEntries(
            EDITABLE_CLIENT_FIELDS.filter(
              (f) => f !== "name" && f !== "constitution",
            ).map((f) => [f, nullIfBlank(form[f])]),
          ),
          services_availed: form.services_availed,
        });
        toast.success("Client created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(
        errorMessage(
          e,
          isEdit ? "Failed to update client" : "Failed to create client",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const testid = (name) => `${isEdit ? "edit" : "new"}-client-${name}`;

  const field = (
    label,
    name,
    input,
    { required = false, optional = false } = {},
  ) => (
    <div className="space-y-1.5">
      <Label className="text-xs" htmlFor={testid(name)}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {optional && (
          <span className="text-muted-foreground font-normal ml-1">
            (optional)
          </span>
        )}
      </Label>
      {input}
    </div>
  );

  const text = (name, extra = {}) => (
    <Input
      id={testid(name)}
      value={form[name]}
      onChange={(extra.upper ? setUpper : set)(name)}
      maxLength={CLIENT_FIELD_MAX[name]}
      placeholder={extra.placeholder}
      type={extra.type}
      className={extra.mono ? "font-mono" : undefined}
      data-testid={testid(name)}
    />
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-y-auto"
        data-testid={isEdit ? "edit-client-dialog" : "new-client-dialog"}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit client" : "New client"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Changes are recorded on this client's history. The client code (${client.client_code}) cannot be changed.`
              : "Only the client code and legal name are required — the rest can be filled in later."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {field(
              "Client code",
              "code",
              <Input
                id={testid("code")}
                value={isEdit ? client.client_code : code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={isEdit}
                maxLength={CLIENT_FIELD_MAX.client_code}
                placeholder="C-001"
                className="font-mono"
                data-testid={testid("code")}
              />,
              { required: true },
            )}
            <div className="sm:col-span-2">
              {field(
                "Legal name",
                "name",
                text("name", { placeholder: "As it appears on the PAN card" }),
                { required: true },
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Constitution</Label>
              <Select
                value={form.constitution}
                onValueChange={(v) => setForm({ ...form, constitution: v })}
              >
                <SelectTrigger data-testid={testid("constitution")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONSTITUTIONS.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">
                      {c.replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {field(
              "PAN",
              "pan",
              text("pan", {
                upper: true,
                mono: true,
                placeholder: "ABCDE1234F",
              }),
              { optional: true },
            )}
            {field(
              "GSTIN",
              "gstin",
              text("gstin", {
                upper: true,
                mono: true,
                placeholder: "22ABCDE1234F1Z5",
              }),
              { optional: true },
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {field("Email", "email", text("email", { type: "email" }))}
            {field(
              "Mobile",
              "mobile",
              text("mobile", { placeholder: "+91 98…" }),
            )}
            {field("Phone (office)", "phone", text("phone"))}
          </div>

          {field(
            "Address",
            "address",
            <Textarea
              id={testid("address")}
              value={form.address}
              onChange={set("address")}
              maxLength={CLIENT_FIELD_MAX.address}
              rows={2}
              data-testid={testid("address")}
            />,
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {field("City", "city", text("city"))}
            {field("State", "state", text("state"))}
            {field("Pincode", "pincode", text("pincode", { mono: true }))}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Services availed</Label>
            <div className="flex flex-wrap gap-1.5">
              {SERVICES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleService(s)}
                  data-testid={testid(
                    `service-${s.toLowerCase().replace(/\s+/g, "-")}`,
                  )}
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                    form.services_availed.includes(s)
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {identityChange && (
            <div
              className="space-y-1.5 rounded-md border border-amber-500/40 bg-amber-500/5 p-3"
              data-testid="edit-client-reason-block"
            >
              <Label className="text-xs flex items-center gap-1.5">
                <AlertTriangle size={12} className="text-amber-500" />
                Reason for the PAN / GSTIN change
                <span className="text-muted-foreground font-normal">
                  (min 5 chars, stored in audit log)
                </span>
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Corrected typo against the PAN card on file"
                data-testid="edit-client-reason"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid={isEdit ? "edit-client-submit" : "new-client-submit"}
          >
            {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
            {isEdit ? "Save changes" : "Create client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
